import React, { useState, useEffect, useCallback } from 'react';
import { evolution } from '../services/api';
import QRCodeDisplay from './QRCodeDisplay';
import BrandCredit from './BrandCredit';
import './Dashboard.css';

function Dashboard({ user, onLogout, onOpenAdmin }) {
  const [instances, setInstances] = useState([]);
  const [instancesLoaded, setInstancesLoaded] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [status, setStatus] = useState(null);
  const [info, setInfo] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showQR, setShowQR] = useState(false);

  const storageKey = `selectedInstance_${user.id}`;

  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const response = await evolution.listMine();
        const list = response.data;
        setInstances(list);

        const savedId = localStorage.getItem(storageKey);
        const savedStillValid = list.some((i) => String(i.id) === savedId);
        const nextId = savedStillValid ? Number(savedId) : list[0]?.id ?? null;

        setSelectedInstanceId(nextId);
        if (nextId) {
          localStorage.setItem(storageKey, String(nextId));
        }
      } catch (err) {
        console.error('Erro ao listar instâncias:', err);
      } finally {
        setInstancesLoaded(true);
      }
    };

    fetchInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectInstance = (id) => {
    setSelectedInstanceId(id);
    localStorage.setItem(storageKey, String(id));
    setStatus(null);
    setInfo(null);
    setShowQR(false);
    setQrCode(null);
  };

  const fetchStatus = useCallback(async () => {
    if (!selectedInstanceId) return;
    try {
      const response = await evolution.getStatus(selectedInstanceId);
      setStatus(response.data);
      setError('');
    } catch (err) {
      console.error('Erro ao obter status:', err);
    }
  }, [selectedInstanceId]);

  const fetchInfo = useCallback(async () => {
    if (!selectedInstanceId) return;
    try {
      const response = await evolution.getInfo(selectedInstanceId);
      setInfo(response.data);
      setError('');
    } catch (err) {
      console.error('Erro ao obter informações:', err);
    }
  }, [selectedInstanceId]);

  useEffect(() => {
    if (!selectedInstanceId) return undefined;

    fetchStatus();
    fetchInfo();
    const interval = setInterval(() => {
      fetchStatus();
      fetchInfo();
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedInstanceId, fetchStatus, fetchInfo]);

  const handleGenerateQR = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await evolution.getQRCode(selectedInstanceId);
      setQrCode(response.data);
      setShowQR(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar QR Code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Deseja realmente desconectar a instância?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await evolution.disconnect(selectedInstanceId);
      alert('Instância desconectada com sucesso');
      fetchStatus();
      fetchInfo();
      setShowQR(false);
      setQrCode(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao desconectar');
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = async () => {
    if (!window.confirm('Deseja realmente reiniciar a instância?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await evolution.restart(selectedInstanceId);
      alert('Instância reiniciada com sucesso');
      fetchStatus();
      fetchInfo();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao reiniciar');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    if (!status) return 'gray';
    const state = status.state || status.instance?.state;
    if (state === 'open') return 'green';
    if (state === 'connecting') return 'orange';
    return 'red';
  };

  const getStatusText = () => {
    if (!status) return 'Carregando...';
    const state = status.state || status.instance?.state;
    if (state === 'open') return 'Conectado';
    if (state === 'connecting') return 'Conectando...';
    if (state === 'close') return 'Desconectado';
    return state || 'Desconhecido';
  };

  const getPhoneNumber = () => {
    if (!info) return 'Não conectado';
    // /instance/fetchInstances retorna um array na maioria das versões da Evolution
    // API; em outras vem como objeto único — tratamos os dois casos.
    const entry = (Array.isArray(info) ? info[0] : info) || {};
    const instance = entry.instance || entry;

    // O número vem como JID (ex: "5511999999999@s.whatsapp.net") em campos que
    // variam entre versões da Evolution API — tenta os nomes mais comuns.
    const raw =
      instance.owner ||
      instance.wuid ||
      instance.ownerJid ||
      instance.number ||
      entry.owner ||
      entry.ownerJid ||
      entry.number;

    if (!raw) return 'Não conectado';

    const digits = String(raw).split('@')[0].replace(/\D/g, '');
    return digits ? `+${digits}` : 'Não conectado';
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-brand">
          <span className="brand-mark">Z</span>
          <h1>EvoRouterZap</h1>
        </div>
        <div className="user-info">
          <span className="user-pill">{user.username}</span>
          {onOpenAdmin && (
            <button onClick={onOpenAdmin} className="btn btn-secondary">Admin</button>
          )}
          <button onClick={onLogout} className="btn-logout">Sair</button>
        </div>
      </header>

      <main className="dashboard-content">
        {error && <div className="error-message">{error}</div>}

        {!instancesLoaded ? (
          <div className="status-card"><p>Carregando instâncias...</p></div>
        ) : instances.length === 0 ? (
          <div className="status-card">
            <h2>Nenhuma instância atribuída</h2>
            <p>Contate um administrador para que uma instância seja criada para você.</p>
          </div>
        ) : (
          <>
            {instances.length > 1 && (
              <div className="instance-selector">
                <label htmlFor="instance-select"><strong>Instância:</strong></label>
                <select
                  id="instance-select"
                  value={selectedInstanceId ?? ''}
                  onChange={(e) => handleSelectInstance(Number(e.target.value))}
                >
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="status-card">
              <h2>Status da Instância</h2>
              <div className="status-info">
                <div className="status-indicator">
                  <div className={`status-dot ${getStatusColor()}`}></div>
                  <span className="status-text">{getStatusText()}</span>
                </div>
                <div className="info-item">
                  <strong>Número WhatsApp:</strong>
                  <span>{getPhoneNumber()}</span>
                </div>
              </div>
            </div>

            <div className="actions-card">
              <h2>Ações</h2>
              <div className="action-buttons">
                <button
                  onClick={handleGenerateQR}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Gerando...' : 'Gerar QR Code'}
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={loading}
                  className="btn btn-warning"
                >
                  Desconectar
                </button>
                <button
                  onClick={handleRestart}
                  disabled={loading}
                  className="btn btn-secondary"
                >
                  Reiniciar
                </button>
              </div>
            </div>

            {showQR && qrCode && (
              <QRCodeDisplay
                qrCode={qrCode}
                onClose={() => {
                  setShowQR(false);
                  fetchStatus();
                  fetchInfo();
                }}
              />
            )}
          </>
        )}

        <BrandCredit />
      </main>
    </div>
  );
}

export default Dashboard;
